/**
 * EGEngine - the audio side of the page: context, strings, rig, band, takes.
 *
 * GRAPH
 *   strings (AudioWorklet 'eg-guitar', ch0 DI / ch1 envelope)
 *        -> rig (pedals, amp, cabinet, studio) ----------------\
 *   real guitar (MicManager) -> gate -> rig.post -------------+-> master -> safety -> out
 *   band: drums (VCSL samples) + bass (second 'eg-guitar' node,
 *         bass model) -> bass rig ----------------------------/
 *
 * TAKES ARE PERFORMANCES, NOT RECORDINGS OF THE SPEAKERS
 * ------------------------------------------------------
 * What you play is logged as events (and, for a real guitar, as the dry DI
 * signal). The export re-renders that performance offline through the rig as
 * it is AT EXPORT TIME. Two consequences a player can hear: the file has none
 * of the realtime constraints (no dropouts, full quality), and you can play a
 * riff once and then hear it through every genre - the re-amping studios pay
 * for, as a button.
 */
(function (root, factory) {
    var api = factory();
    if (typeof globalThis !== 'undefined') globalThis.EGEngine = api;
    if (typeof window !== 'undefined') window.EGEngine = api;
}(this, function () {
    'use strict';

    function clamp(v, lo, hi) { return v < lo ? lo : (v > hi ? hi : v); }
    function midiToHz(m) { return 440 * Math.pow(2, (m - 69) / 12); }

    // ───────────────────────────── riffs → events ─────────────────────────
    /*
     * Expand a riff (EGPresets notation) into core events with times in
     * seconds from 0, plus tab cells for the display. `tuning` is the open
     * MIDI notes, index 0 = string 1. Power chords are built by PITCH
     * (fifth + octave), so they are right in drop and seven-string tunings.
     */
    function expandRiff(riff, tuning, opts) {
        opts = opts || {};
        var bpm = opts.bpm || riff.bpm;
        var spb = 60 / bpm;
        var t0 = opts.t0 || 0;
        var human = opts.human === undefined ? 1 : opts.human;
        var seed = opts.seed || 11;
        function rnd() { seed = (seed * 16807) % 2147483647; return seed / 2147483647; }
        var n = tuning.length;
        var events = [], cells = [];
        function fretFor(si, midi) {
            if (si < 0 || si >= n) return -1;
            var f = midi - tuning[si];
            return (f >= 0 && f <= 24) ? f : -1;
        }
        for (var i = 0; i < riff.notes.length; i++) {
            var nt = riff.notes[i];
            var beat = nt[0], str = nt[1], fret = nt[2], len = nt[3], flags = (nt[4] || '').split(' ');
            var has = {}, chordStr = null, slideTo = null;
            for (var k = 0; k < flags.length; k++) {
                var f = flags[k];
                if (!f) continue;
                if (f.indexOf('c:') === 0) chordStr = f.slice(2);
                else if (f.charAt(0) === 's' && /^s\d+$/.test(f)) slideTo = +f.slice(1);
                else has[f] = true;
            }
            var s = str - 1;
            var t = Math.max(t0, t0 + beat * spb + (human ? (rnd() - 0.5) * 0.008 * human : 0));
            var accent = (beat % 1) === 0 ? 0.06 : 0;
            var vel = clamp(0.8 + accent + (human ? (rnd() - 0.5) * 0.1 * human : 0), 0.2, 1);
            var o = {};
            if (has.pm) o.pm = 0.85;
            if (has.x) o.dead = true;
            if (has.ph) o.pinch = true;
            if (has.h) o.legato = true;
            if (has.nh) o.harm = fret === 12 ? 2 : fret === 7 || fret === 19 ? 3 : fret === 5 ? 4 : fret === 4 || fret === 9 ? 5 : 2;
            var endT = t0 + (beat + len) * spb;
            var strings = [];
            if (chordStr) {
                // chord shape, low string first
                var shape = chordStr.split('');
                for (var c = 0; c < shape.length; c++) {
                    var si = n - 1 - c;
                    if (si < 0) break;
                    if (shape[c] === 'x') { if (has.x) strings.push([si, 0]); continue; }
                    strings.push([si, parseInt(shape[c], 36)]);
                }
                if (has.x && !strings.length) for (var q = 0; q < n; q++) strings.push([q, 0]);
            } else if (has['5']) {
                var rootMidi = tuning[s] + fret;
                strings.push([s, fret]);
                var f5 = fretFor(s - 1, rootMidi + 7); if (f5 >= 0) strings.push([s - 1, f5]);
                var f8 = fretFor(s - 2, rootMidi + 12); if (f8 >= 0 && !has.pm) strings.push([s - 2, f8]);
            } else {
                strings.push([s, fret]);
            }
            // strum order: downstroke = low to high, upstroke = high to low
            strings.sort(function (a, b) { return has.u ? a[0] - b[0] : b[0] - a[0]; });
            var spread = strings.length > 1 ? (has.x ? 0.005 : 0.0055 + rnd() * 0.004) : 0;
            var tremolo = has.t;
            for (var j = 0; j < strings.length; j++) {
                var st = strings[j][0], fr = strings[j][1];
                var tt = t + j * spread;
                var v = clamp(vel * (1 - j * 0.03) * (has.u ? 0.85 : 1), 0.15, 1);
                if (tremolo) {
                    /* alternate picking: every other pick is an upstroke (the
                       string is pushed the other way) and no two picks land or
                       hit alike - a metronomic, identical re-pick is the
                       machine-gun buzz a tagger hears as "Synthesizer" */
                    var step = spb / 4, m = 0, oUp = Object.assign({}, o, { up: true });
                    for (var tp = tt; tp < endT - 0.01; tp += step, m++) {
                        var jt = human ? (rnd() - 0.5) * 0.007 * human : 0;
                        var vj = human ? 1 + (rnd() - 0.5) * 0.16 * human : 1;
                        events.push({ t: Math.max(t0, tp + jt), type: 'on', s: st, fret: fr, vel: clamp(v * (m % 2 ? 0.8 : 1) * vj, 0.15, 1), o: m % 2 ? oUp : o });
                    }
                } else {
                    events.push({ t: tt, type: 'on', s: st, fret: fr, vel: v, o: has.u ? Object.assign({}, o, { up: true }) : o });
                }
                cells.push({ beat: beat, s: st, fret: fr, len: len, flags: nt[4] || '' });
                if (has.b1 || has.b2) {
                    var semis = has.b2 ? 2 : 1;
                    events.push({ t: tt + Math.min(0.09, len * spb * 0.25), type: 'bend', s: st, semis: semis, sec: Math.min(0.16, len * spb * 0.4) });
                }
                if (has.v) events.push({ t: tt + len * spb * 0.35, type: 'vib', s: st, depth: 0.28, rate: 5.6 });
                if (slideTo !== null) events.push({ t: tt + len * spb * 0.45, type: 'slide', s: st, fret: slideTo, sec: Math.min(0.15, len * spb * 0.4) });
                if (!riff.ring && !has.x) {
                    events.push({ t: endT - 0.004, type: 'off', s: st, rel: has.pm ? 0.05 : 0.08 });
                }
                if (has.v) events.push({ t: endT - 0.005, type: 'vib', s: st, depth: 0 });
                if (has.b1 || has.b2) events.push({ t: endT + 0.002, type: 'bend', s: st, semis: 0, sec: 0.02 });
            }
        }
        events.sort(function (a, b) { return a.t - b.t; });
        return { events: events, cells: cells, seconds: t0 + riff.beats * spb, spb: spb };
    }

    // ─────────────────────────────── WAV ──────────────────────────────────
    function wavBlob(buffer, bits) {
        bits = bits || 16;
        var ch = buffer.numberOfChannels, n = buffer.length, sr = buffer.sampleRate;
        var bps = bits / 8, size = 44 + n * ch * bps;
        var ab = new ArrayBuffer(size), v = new DataView(ab);
        function w(o, s) { for (var i = 0; i < s.length; i++) v.setUint8(o + i, s.charCodeAt(i)); }
        w(0, 'RIFF'); v.setUint32(4, size - 8, true); w(8, 'WAVE'); w(12, 'fmt ');
        v.setUint32(16, 16, true); v.setUint16(20, 1, true); v.setUint16(22, ch, true);
        v.setUint32(24, sr, true); v.setUint32(28, sr * ch * bps, true); v.setUint16(32, ch * bps, true);
        v.setUint16(34, bits, true); w(36, 'data'); v.setUint32(40, n * ch * bps, true);
        var data = []; for (var c = 0; c < ch; c++) data.push(buffer.getChannelData(c));
        var o = 44;
        for (var i = 0; i < n; i++) {
            for (var k = 0; k < ch; k++) {
                var x = clamp(data[k][i], -1, 1);
                if (bits === 24) {
                    var s24 = Math.round(x * 8388607);
                    v.setUint8(o, s24 & 255); v.setUint8(o + 1, (s24 >> 8) & 255); v.setUint8(o + 2, (s24 >> 16) & 255); o += 3;
                } else { v.setInt16(o, Math.round(x * 32767), true); o += 2; }
            }
        }
        return new Blob([ab], { type: 'audio/wav' });
    }

    // ─────────────────────────────── engine ───────────────────────────────
    function Engine(cfg) {
        this.cfg = cfg || {};
        this.ctx = null;
        this.ready = false;
        this.node = null;            // guitar worklet node (or script processor)
        this.bassNode = null;
        this.rig = null;
        this.bassRig = null;
        this.jam = null;
        this.guitar = { model: 'lp', sel: 'b', tuning: 'std', capo: 0, pickMm: 85, sympathy: 0.35, intonation: 1 };
        this.rigState = null;
        this.take = null;            // recording in progress
        this.lastTake = null;
        this.onSound = null;
        this.fallback = false;
        this.input = null;           // real guitar path
        this.listeners = {};
    }
    Engine.prototype.on = function (ev, fn) { (this.listeners[ev] = this.listeners[ev] || []).push(fn); };
    Engine.prototype.emit = function (ev, d) { (this.listeners[ev] || []).forEach(function (f) { try { f(d); } catch (e) {} }); };

    /* The graph in pieces: prepare() builds one per idle callback, a start
       that finds some still waiting builds the rest at once. */
    Engine.prototype._pieces = function (ctx) {
        var self = this, cfg = this.cfg;
        return [
            function () {
                self.ctx = ctx;
                self.master = ctx.createGain();
                self.master.gain.value = 0.9;
                self.safety = new window.EGRig.Safety(ctx, -1.5);
                // the level meter is optional: EGRig.analyser never throws (a tampered prototype)
                self.analyser = window.EGRig.analyser(ctx, 2048);
                self.master.connect(self.safety.input); self.safety.output.connect(ctx.destination);
                if (self.analyser) self.safety.output.connect(self.analyser);
                self.jamBus = ctx.createGain(); self.jamBus.gain.value = 0.75;
                self.jamBus.connect(self.master);
            },
            function () {
                // made with the state it will play: the preset picked before the first touch
                self.rig = new window.EGRig.Rig(ctx, { state: self.rigState || undefined });
                self.rig.output.connect(self.master);
                if (self.rig.cabError) self.emit('asset', { kind: 'cab_refused', failed: 1 });
            },
            function () {
                self.bassRig = new window.EGRig.Rig(ctx, { state: bassRigState() });
                self.bassRig.output.connect(self.jamBus);
                var P = window.EGPresets;
                self.jam = new window.EGJam.Jam({
                    ctx: ctx, presets: P, drumsBase: cfg.drumsBase, manifestUrl: cfg.drumsManifest,
                    bassPlay: function (list) { self._bassEvents(list); },
                    onBar: function (n, bar) { self.emit('bar', { n: n, bar: bar }); },
                    onBeat: function (n, b) { self.emit('beat', { n: n, beat: b }); }
                });
                self.jam.out.connect(self.jamBus);
                if (self.jam.drums) self.jam.drums.onReport = function (r) { self.emit('asset', r); };
                self.jam.onHit = function (h) { self._takeDrum(h); };
            }
        ];
    };
    /* Build or rebuild everything on `ctx`, now. */
    Engine.prototype._build = function (ctx) {
        this._watch(ctx);
        this._pieces(ctx).forEach(function (f) { f(); });
    };
    /* A context is "proven" once it has run: from then on a refused resume
       means something is wrong with it, before that it only means the browser
       has not been given a gesture yet. */
    Engine.prototype._watch = function (ctx) {
        if (ctx.__egWatched) return;
        ctx.__egWatched = true;
        var self = this;
        var on = function () {
            if (ctx.state !== 'running') return;
            ctx.__egRan = true;
            self._flushHeld(ctx);
        };
        try { ctx.addEventListener('statechange', on); } catch (e) {}
        on();
    };
    function activeNow() {
        try { var ua = navigator.userActivation; return !ua || typeof ua.isActive !== 'boolean' ? true : ua.isActive; } catch (e) { return true; }
    }
    /*
     * Resume inside the event that carries the permission. On a touch screen
     * the HTML rule gives that permission on the LIFT (pointerup/touchend),
     * not on the touch; a browser that follows it refuses a resume from
     * pointerdown. timbrica-core's __liveAudioCtxReady reads such a refusal,
     * once the page has had any gesture, as a context that lost its device and
     * replaces it - right for a context that ran, wrong for one that never had
     * the chance. So a context that has never run is resumed plainly when there
     * is no live activation, and the page resumes it again on the lift.
     */
    Engine.prototype._resumeGate = function (ctx) {
        if (!ctx.__egRan && ctx.state !== 'running' && !activeNow()) {
            try { var p = ctx.resume(); if (p && p.catch) p.catch(function () {}); } catch (e) {}
            return Promise.resolve(ctx);
        }
        if (window.__liveAudioCtxReady) return Promise.resolve(window.__liveAudioCtxReady(ctx));
        if (ctx.state === 'suspended') { try { ctx.resume(); } catch (e) {} }
        return Promise.resolve(ctx);
    };
    /* From an event that carries user activation (the page's lift hook).
       A started engine goes through wake(): with the activation live, the
       site's check is the right judge again, and a dead context is replaced
       and named instead of staying silent. */
    Engine.prototype.unlock = function () {
        var c = this.ctx || (this._prep && this._prep.ctx);
        if (!c || c.state === 'running' || c.state === 'closed') return;
        if (this.ready && activeNow()) { this.wake().catch(function () {}); return; }
        try { var p = c.resume(); if (p && p.catch) p.catch(function () {}); } catch (e) {}
    };

    /*
     * PREPARE BEFORE THE FIRST TOUCH. The page calls this when the neck is on
     * screen and the browser is idle.
     * The first touch used to create the context, build both rigs and the
     * band, and only then fetch the strings' worklet: 1.8 s of main thread
     * inside one interaction at 4x CPU, and in a slow moment up to 2.4 s more
     * before a sound (six cabinet files went out first and the module queued
     * behind them). Here the context is created - without a gesture a browser
     * keeps it suspended: nothing plays, nothing is spent - the graph is built
     * one piece per idle callback, the strings' module goes out first and the
     * cabinets after it. The first touch then only resumes; a touch in the
     * middle of this builds the remaining pieces at once.
     */
    Engine.prototype.prepare = function () {
        if (this._prep) return this._prep.done;
        if (this._starting || this.ctx) return Promise.resolve(this);
        var Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return Promise.resolve(null);
        var ctx;
        try { ctx = new Ctx({ latencyHint: 'interactive' }); } catch (e) { return Promise.resolve(null); }
        var self = this, t0 = performance.now();
        this._watch(ctx);
        var prep = this._prep = { ctx: ctx, queue: this._pieces(ctx), t0: t0 };
        var built = new Promise(function (res, rej) { prep.built = res; prep.fail = rej; });
        var modules = this._loadModules(ctx);
        modules.then(function () { if (self._prep === prep) self._loadCabs(ctx).catch(function () {}); });
        prep.done = Promise.all([built, modules]).then(function (r) {
            if (self._prep !== prep) return self;          // its context died and was replaced
            self._makeNodes(ctx, r[1]);
            prep.ms = Math.round(performance.now() - t0);
            self.emit('prepared', { ms: prep.ms, state: ctx.state });
            return self;
        }, function (e) {
            if (self._prep === prep) self._prep = null;    // the next start builds from scratch
            throw e;
        });
        prep.done.catch(function () {});
        var idle = window.requestIdleCallback
            ? function (f) { window.requestIdleCallback(f, { timeout: 1500 }); }
            : function (f) { setTimeout(f, 60); };
        function step() {
            if (self._prep !== prep || !prep.queue.length) return;
            try { prep.queue.shift()(); } catch (e) { prep.queue.length = 0; prep.fail(e); return; }
            if (prep.queue.length) idle(step); else prep.built();
        }
        // the rigs are built once the module is in: their changing filters are
        // then eg-filter nodes (a change is a message) instead of IIRFilterNodes
        modules.then(function () { idle(step); });
        return prep.done;
    };
    Engine.prototype._finishPieces = function () {
        var prep = this._prep;
        if (!prep || !prep.queue.length) return;
        try { while (prep.queue.length) prep.queue.shift()(); prep.built(); }
        catch (e) { prep.queue.length = 0; prep.fail(e); }
    };
    Engine.prototype.prepared = function () {
        var prep = this._prep;
        return prep ? (prep.ms !== undefined ? 'ready' : (prep.queue.length ? 'building' : 'loading')) : 'none';
    };

    /*
     * Start (on a user gesture). Resolves when the strings can sound.
     * AudioWorklet first; if the browser has none or the module fails to load,
     * the same core runs in a ScriptProcessor on the page - worse latency,
     * same sound.
     */
    Engine.prototype.start = function () {
        var self = this;
        if (this._starting) return this._starting;
        var Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return Promise.reject(new Error('no-webaudio'));
        var prep = this._prep;
        var ctx = prep ? prep.ctx : (this.ctx || new Ctx({ latencyHint: 'interactive' }));
        this._watch(ctx);
        var live = this._resumeGate(ctx);                  // first: we are inside the gesture
        if (prep) this._finishPieces();
        this._starting = live.then(function (c) {
            if (prep && c === prep.ctx && self._prep === prep) return prep.done;
            // nothing prepared, or its context died and came back as a new one
            self._prep = null;
            self._build(c);
            return self._loadBoth(c);
        }).then(function () {
            self.ready = true;
            self.emit('ready', { fallback: self.fallback, latency: self.latencyMs() });
            return self;
        }).catch(function (e) {
            self._starting = null;
            throw e;
        });
        return this._starting;
    };

    /* On every gesture: a context that lost its device comes back as a new
       object (timbrica-core); then everything is rebuilt on it. */
    Engine.prototype.wake = function () {
        var self = this;
        if (!this.ctx) return this.start();
        return this._resumeGate(this.ctx).then(function (c) {
            if (c !== self.ctx) {
                /* The device was lost and the context replaced: the new strings get the
                   guitar, noise and "let ring" the old ones had, a take in progress keeps
                   its clock (its time continues from the wall clock), and the page is
                   told ('rebuilt') to restore levels and the band. All of this used to
                   reset silently - defaults, a silent band under "Stop band", and every
                   later note of the take dropped at a negative time. */
                var tk = self.take;
                self.ready = false; self._starting = null; self._prep = null; self.ctx = c;
                self._build(c);
                return self._loadBoth(c).then(function () {
                    self.ready = true;
                    if (tk && tk.wall !== undefined) tk.start = c.currentTime - (performance.now() - tk.wall) / 1000;
                    self._resync();
                    self.emit('rebuilt', {});
                    return self;
                });
            }
            return self;
        });
    };

    /* The strings' module first, the measured cabinets after it: six cabinet
       files sent first took every connection of an HTTP/1.1 origin and the
       module the first note waits for queued behind them (up to 1.5 s,
       measured on the dev origin). A cabinet that is not there within 1.2 s
       after the strings are ready plays through the synthetic one and is
       swapped in on arrival. */
    Engine.prototype._loadBoth = function (ctx) {
        var self = this;
        var modules = this._loadModules(ctx);
        var cabs = modules.then(function () { return self._loadCabs(ctx); }).catch(function () {});
        return modules.then(function (ok) {
            self._makeNodes(ctx, ok);
            return Promise.race([cabs, new Promise(function (r) { setTimeout(r, 1200); })]);
        });
    };

    /* A page that is being left aborts its downloads: those are not lost
       files. Measured: of the three "every cabinet failed" reports in October
       one was a tab reloaded five times in four seconds. */
    var leaving = false;
    try { window.addEventListener('pagehide', function () { leaving = true; }); } catch (e) {}
    Engine.prototype._loadCabs = function (ctx) {
        var base = this.cfg.irBase, keys = window.EGRig.CAB_KEYS, self = this, failed = 0, why = '';
        if (!base || this._cabsLoaded === ctx) return Promise.resolve();
        this._cabsLoaded = ctx;
        return Promise.all(keys.map(function (k) {
            var stage = 'net';
            return fetch(base + 'cab-' + k + '.wav' + (self.cfg.irVersion ? '?v=' + self.cfg.irVersion : ''), { credentials: 'same-origin' })
                .then(function (r) { if (!r.ok) { stage = 'http' + r.status; throw new Error('HTTP ' + r.status); } stage = 'body'; return r.arrayBuffer(); })
                .then(function (ab) { stage = 'decode'; return new Promise(function (res, rej) { var p = ctx.decodeAudioData(ab, res, rej); if (p && p.then) p.then(res, rej); }); })
                .then(function (buf) {
                    window.EGRig.registerCab(k, buf.getChannelData(0).slice(0), buf.sampleRate);
                }).catch(function (e) {
                    failed++;
                    if (!why) why = (e && e.name === 'AbortError') ? 'abort' : stage;
                });
        })).then(function () {
            // a lost file is audible only as a duller cabinet (the synthetic one) - say so
            if (failed && !leaving && why !== 'abort') self.emit('asset', { kind: 'cab', failed: failed, why: why });
            // re-apply the current cab so the measured IR replaces the synthetic one
            // (a rig that is built later finds the measured IR by itself)
            if (self.rig && self.rigState && self.rigState.cab) self.rig.setCab(self.rigState.cab.type);
            if (self.bassRig) self.bassRig.setCab('g12f');
        });
    };

    /* Resolves true when the strings' processor is registered on `ctx`. */
    Engine.prototype._loadModules = function (ctx) {
        var cfg = this.cfg, loader = window.AudioWorkletLoader;
        if (ctx.__egModules) return ctx.__egModules;
        var canWorklet = ctx.audioWorklet && loader && !cfg.forceFallback;
        return (ctx.__egModules = canWorklet
            ? loader.load(ctx, cfg.urls.core, { slug: cfg.slug }).then(function () { return loader.load(ctx, cfg.urls.worklet, { slug: cfg.slug }); })
                .then(function () { ctx.__egFilter = true; return true; }, function () { return false; })
            : Promise.resolve(false));
    };
    Engine.prototype._makeStrings = function (ctx) {
        var self = this;
        return this._loadModules(ctx).then(function (ok) { self._makeNodes(ctx, ok); });
    };
    Engine.prototype._makeNodes = function (ctx, ok) {
        var self = this;
        if (this.node && this.node.context === ctx) return;
        {
            if (ok) {
                self.node = new AudioWorkletNode(ctx, 'eg-guitar', {
                    numberOfInputs: 0, numberOfOutputs: 1, outputChannelCount: [2],
                    processorOptions: { model: self.guitar.model, sel: self.guitar.sel, tuning: self._tuningArr() }
                });
                self.node.port.onmessage = function (e) { if (e.data && e.data.type === 'sound') self.emit('sound', {}); };
                self.bassNode = new AudioWorkletNode(ctx, 'eg-guitar', {
                    numberOfInputs: 0, numberOfOutputs: 1, outputChannelCount: [2],
                    processorOptions: { model: 'bass', tuning: window.EGCore.TUNINGS.bass4, seed: 777 }
                });
                self.fallback = false;
            } else {
                self.node = self._scriptNode(ctx, self.guitar.model, self._tuningArr(), 1234);
                self.bassNode = self._scriptNode(ctx, 'bass', window.EGCore.TUNINGS.bass4, 777);
                self.fallback = true;
            }
            var split = ctx.createChannelSplitter(2);
            self.node.connect(split);
            split.connect(self.rig.input, 0);
            split.connect(self.rig.envIn, 1);
            var bsplit = ctx.createChannelSplitter(2);
            self.bassNode.connect(bsplit);
            bsplit.connect(self.bassRig.input, 0);
            self._send([{ type: 'model', model: self.guitar.model, sel: self.guitar.sel },
                { type: 'tuning', tuning: self._tuningArr() }, { type: 'capo', capo: self.guitar.capo },
                { type: 'pick', mm: self.guitar.pickMm }, { type: 'sympathy', v: self.guitar.sympathy },
                { type: 'intonation', v: self.guitar.intonation }]);
            self._sendBass([{ type: 'model', model: 'bass', sel: 'p' }, { type: 'tuning', tuning: window.EGCore.TUNINGS.bass4 }, { type: 'sympathy', v: 0 }]);
        }
    };

    /* ScriptProcessor fallback: the same core on the main thread. */
    Engine.prototype._scriptNode = function (ctx, model, tuning, seed) {
        var g = new window.EGCore.Guitar(ctx.sampleRate, { seed: seed });
        g.setModel(model); g.setTuning(tuning);
        var node = ctx.createScriptProcessor(512, 0, 2);
        var env = new Float32Array(512);
        var self = this, told = false;
        node.onaudioprocess = function (e) {
            var o0 = e.outputBuffer.getChannelData(0), o1 = e.outputBuffer.getChannelData(1);
            g.frame = Math.round(e.playbackTime * ctx.sampleRate);
            g.process(o0, o0.length, env);
            o1.set(env.subarray(0, o1.length));
            if (!told && model !== 'bass') { for (var i = 0; i < o0.length; i++) if (o0[i] > 1e-3 || o0[i] < -1e-3) { told = true; self.emit('sound', {}); break; } }
        };
        node._core = g;
        node.port = { postMessage: function (d) {
            if (d.type !== 'ev') return;
            d.list.forEach(function (ev) { ev.f = ev.t ? Math.round(ev.t * ctx.sampleRate) : 0; g.schedule(ev); });
        } };
        return node;
    };

    Engine.prototype._tuningArr = function () {
        var T = window.EGCore.TUNINGS;
        return (T[this.guitar.tuning] || T.std).slice();
    };
    Engine.prototype.tuning = function () { return this._tuningArr(); };

    Engine.prototype._send = function (list) {
        if (!this.node) return;
        this.node.port.postMessage({ type: 'ev', list: list });
    };
    // the guitar state, again, to strings that were just built on a new context
    Engine.prototype._resync = function () {
        var g = this.guitar || {}, ev = [];
        if (g.model) ev.push({ type: 'model', model: g.model, sel: g.sel });
        if (g.tuning) ev.push({ type: 'tuning', tuning: this._tuningArr() });
        ev.push({ type: 'capo', capo: g.capo || 0 });
        if (g.pickMm) ev.push({ type: 'pick', mm: g.pickMm });
        if (g.sympathy !== undefined) ev.push({ type: 'sympathy', v: g.sympathy });
        if (this.noise) ev.push({ type: 'noise', v: this.noise.v, hz: this.noise.hz });
        if (this._ring) ev.push({ type: 'ring', on: true });
        this._send(ev);
    };
    /* Noise and hum go through here, not a raw _send: a take has to know them.
       The export used to render with the core's defaults (noise 0.6, 50 Hz) -
       a person who had turned the noise off or picked 60 Hz got it back in
       the file. */
    Engine.prototype.setNoise = function (v, hz) {
        this.noise = { v: v, hz: hz };
        var ev = { type: 'noise', v: v, hz: hz };
        if (this.take) this.take.events.push(Object.assign({ t: this.ctx.currentTime - this.take.start }, ev));
        this._send([ev]);
    };
    Engine.prototype._sendBass = function (list) {
        if (!this.bassNode) return;
        this.bassNode.port.postMessage({ type: 'ev', list: list });
    };
    /* What the band plays during a take is recorded as it is scheduled - the
       export replays it. It used to be recomputed from a snapshot of the band
       taken when the take started, counted from a band start that lay BEFORE
       the take: every hit came out at a negative time and was stacked at 0 - one
       clipped burst of the whole band's drums, then silence. A band started in
       the middle of a take was not exported at all. */
    Engine.prototype._takeDrum = function (h) {
        var tk = this.take;
        if (!tk || h.t < tk.start) return;
        tk.drums.push({ t: h.t - tk.start, piece: h.piece, vel: h.vel });
    };
    Engine.prototype._bassEvents = function (list) {
        var core = window.EGJam.bassCoreEvents(list);
        this._sendBass(core);
        // the band schedules ahead: a take starting inside that window takes these
        var rec = this._bassRecent || (this._bassRecent = []), now = this.ctx ? this.ctx.currentTime : 0;
        for (var j = 0; j < list.length; j++) rec.push(Object.assign({}, list[j]));
        while (rec.length && rec[0].t < now - 0.5) rec.shift();
        if (this.take) {
            var t0 = this.take.start;
            for (var i = 0; i < list.length; i++) if (list[i].t >= t0) this.take.bass.push(Object.assign({}, list[i], { t: list[i].t - t0 }));
            if (!this.take.bpm && this.jam && this.jam.secPerBeat) this.take.bpm = Math.round(60 / this.jam.secPerBeat);
        }
    };

    // ────────────────────────── playing (live) ────────────────────────────
    /* One public door for everything the hands do. `t` = context time, or
       omitted for "now". Recorded into the take when one is running. */
    Engine.prototype.play = function (evs) {
        if (!this.node) return;
        var ctx = this.ctx, now = ctx.currentTime;
        var list = Array.isArray(evs) ? evs : [evs];
        for (var ri = 0; ri < list.length; ri++) if (list[ri] && list[ri].type === 'ring') this._ring = !!list[ri].on;
        // when the first NOTE was asked for ('on' - a ring or vibrato switch is not one): the
        // latency the player heard is measured from here. askedAt is the gesture's own time when
        // the note waited for the engine to start; warm - the context was already running at
        // that gesture (an earlier gesture anywhere on the page woke it)
        if (!this.firstPlayAt && list.some(function (e) { return e && e.type === 'on'; })) {
            this.firstPlayAt = this.askedAt || performance.now();
            this.firstPlayWarm = this.askedAt ? !!this.askedWarm : ctx.state === 'running';
        }
        /* The first touch on a phone can land before the browser lets the
           context run (it may only allow it on the lift). Its clock stands
           still, so every string of a strum would get the same time and sound
           as one blocked chord on the lift; held here with the wall clock,
           they are played with their own spacing once it runs. */
        if (ctx.state !== 'running' && !ctx.__egRan) {
            var held = this._held || (this._held = []), w = performance.now();
            for (var h = 0; h < list.length; h++) {
                var e0 = list[h];
                held.push({ ev: Object.assign({}, e0), w: e0.t ? w + Math.max(0, e0.t - now) * 1000 : w });
            }
            if (held.length > 256) held.splice(0, held.length - 256);
            return;
        }
        if (this.take) {
            for (var i = 0; i < list.length; i++) {
                var ev = list[i];
                var t = (ev.t || now + 0.004) - this.take.start;
                if (t < 0) continue;
                var c = Object.assign({}, ev); c.t = t;
                this.take.events.push(c);
            }
        }
        this._send(list.map(function (e) { return Object.assign({}, e); }));
    };

    Engine.prototype._flushHeld = function (ctx) {
        var held = this._held;
        if (!held || !held.length || !this.node || ctx !== this.ctx) return;
        this._held = null;
        held.sort(function (a, b) { return a.w - b.w; });
        var w0 = held[0].w, t0 = ctx.currentTime + 0.01;
        var list = held.map(function (x) { var e = x.ev; e.t = t0 + (x.w - w0) / 1000; return e; });
        this.play(list);
        this.emit('held', { n: list.length });
    };

    Engine.prototype.setGuitar = function (g) {
        var changed = [];
        for (var k in g) if (Object.prototype.hasOwnProperty.call(g, k) && this.guitar[k] !== g[k]) { this.guitar[k] = g[k]; changed.push(k); }
        var ev = [];
        if (changed.indexOf('model') >= 0 || changed.indexOf('sel') >= 0) ev.push({ type: 'model', model: this.guitar.model, sel: this.guitar.sel });
        if (changed.indexOf('tuning') >= 0) ev.push({ type: 'tuning', tuning: this._tuningArr() });
        if (changed.indexOf('capo') >= 0) ev.push({ type: 'capo', capo: this.guitar.capo });
        if (changed.indexOf('pickMm') >= 0) ev.push({ type: 'pick', mm: this.guitar.pickMm });
        if (changed.indexOf('sympathy') >= 0) ev.push({ type: 'sympathy', v: this.guitar.sympathy });
        if (ev.length) {
            /* A change during a take goes into the take's own event list, at its
               time: the offline render and the MIDI file replay it where it
               happened. It used to be kept aside and never read - a genre switch
               (a new tuning) or a capo move in the middle of a take exported every
               note with the tuning of the take's first second. */
            if (this.take) {
                var tt = this.ctx.currentTime - this.take.start;
                for (var ci = 0; ci < ev.length; ci++) { var ce = Object.assign({}, ev[ci]); ce.t = tt; this.take.events.push(ce); }
            }
            this._send(ev);
        }
        // the pickup's resonance and output live in the rig
        var M = window.EGCore.MODELS[this.guitar.model];
        var sel = M && M.sel[this.guitar.sel];
        if (sel) this.applyRig({ guitar: { res: sel.res, out: sel.out, tone: this.guitar.tone, vol: this.guitar.vol } });
    };

    Engine.prototype.applyRig = function (st) {
        // keep a merged snapshot for the export and for re-builds
        this.rigState = merge(this.rigState || {}, st);
        if (this.rig) this.rig.apply(st);
        if (st.gate !== undefined && this.input && this.input.gate && this.input.gate.parameters) {
            var thr = this.input.gate.parameters.get('threshold');
            if (thr) thr.setValueAtTime(clamp(st.gate, -96, -20), this.ctx.currentTime);
        }
    };
    function merge(a, b) {
        var out = Array.isArray(a) ? a.slice() : Object.assign({}, a);
        for (var k in b) {
            if (!Object.prototype.hasOwnProperty.call(b, k)) continue;
            var v = b[k];
            out[k] = (v && typeof v === 'object' && !Array.isArray(v) && out[k] && typeof out[k] === 'object') ? merge(out[k], v) : v;
        }
        return out;
    }

    Engine.prototype.latencyMs = function () {
        var c = this.ctx;
        if (!c) return null;
        var base = (c.baseLatency || 0) + (c.outputLatency || 0);
        var block = this.fallback ? 512 / c.sampleRate : 128 / c.sampleRate;
        return Math.round((base + block) * 1000);
    };
    Engine.prototype.level = function () {
        var r = window.EGRig.readLevel(this.analyser, this._lv);
        if (!r) return { peak: 0, rms: 0 };
        this._lv = r.buf;
        return r;
    };

    // ──────────────────────────── riff player ─────────────────────────────
    Engine.prototype.playRiff = function (riff, opts) {
        opts = opts || {};
        var ctx = this.ctx;
        var x = expandRiff(riff, this._tuningArr(), { bpm: opts.bpm, human: 1, seed: opts.seed || 5 });
        var t0 = opts.when || (ctx.currentTime + 0.08);
        var list = x.events.map(function (e) { var c = Object.assign({}, e); c.t = e.t + t0; c.src = 'riff'; return c; });
        this.play(list);
        return { t0: t0, seconds: x.seconds, spb: x.spb, cells: x.cells };
    };
    /* The whole riff is queued in the worklet at once; Stop used to only damp
       what was sounding (muteAll) - the queued notes played on while the button
       read "Listen", and Listen again laid a second riff over the first. The
       notes that will not be heard also leave the take. */
    Engine.prototype.stopRiff = function () {
        this._send([{ type: 'cancel', src: 'riff' }, { type: 'muteAll' }]);
        var tk = this.take;
        if (tk && this.ctx) {
            var cut = this.ctx.currentTime - tk.start;
            for (var i = tk.events.length - 1; i >= 0; i--) { var e = tk.events[i]; if (e.src === 'riff' && e.t > cut) tk.events.splice(i, 1); }
            tk.events.push({ t: Math.max(0, cut), type: 'muteAll' });
        }
    };
    Engine.prototype.stopAll = function () {
        this._send([{ type: 'muteAll' }]);
        this._sendBass([{ type: 'muteAll' }]);
    };
    Engine.prototype.panic = function () { this._send([{ type: 'panic' }]); this._sendBass([{ type: 'panic' }]); };

    // ─────────────────────────────── takes ────────────────────────────────
    Engine.prototype.startTake = function () {
        var ctx = this.ctx, t0 = ctx.currentTime, playing = !!(this.jam && this.jam.playing);
        this.take = {
            start: t0, wall: performance.now(), events: [], bass: [], drums: [], guitar: Object.assign({}, this.guitar),
            jam: playing ? { state: JSON.parse(JSON.stringify(this.jam.band.state)) } : null,
            bpm: playing && this.jam.secPerBeat ? Math.round(60 / this.jam.secPerBeat) : 0,
            bandVol: this.jamBus ? this.jamBus.gain.value : 0.75,
            di: null
        };
        // what the band had already queued for the take's first moments
        var tk = this.take;
        // the state the take starts with, as events at 0 - the render replays them
        if (this.noise) tk.events.push({ t: 0, type: 'noise', v: this.noise.v, hz: this.noise.hz });
        if (this._ring) tk.events.push({ t: 0, type: 'ring', on: true });
        (this.jam && this.jam.recent || []).forEach(function (h) { if (h.t >= t0) tk.drums.push({ t: h.t - t0, piece: h.piece, vel: h.vel }); });
        (this._bassRecent || []).forEach(function (b) { if (b.t >= t0) tk.bass.push(Object.assign({}, b, { t: b.t - t0 })); });
        if (this.input && this.input.recorder) this.input.recorder.port.postMessage({ type: 'arm' });
        this.emit('take', { recording: true });
        return this.take;
    };
    Engine.prototype.stopTake = function () {
        var tk = this.take;
        if (!tk) return Promise.resolve(null);
        this.take = null;
        tk.seconds = this.ctx.currentTime - tk.start;
        var self = this;
        var p = Promise.resolve(tk);
        if (this.input && this.input.recorder) {
            p = new Promise(function (res) {
                var rec = self.input.recorder;
                rec.port.onmessage = function (e) {
                    if (e.data && e.data.type === 'take') { tk.di = e.data.pcm; tk.diRate = e.data.sr; res(tk); }
                };
                rec.port.postMessage({ type: 'disarm' });
                setTimeout(function () { res(tk); }, 1500);
            });
        }
        return p.then(function (t) {
            if (!t.events.length && !t.di) return null;
            self.lastTake = t;
            self.emit('take', { recording: false, take: t });
            return t;
        });
    };

    /*
     * Offline render of a take through the CURRENT rig. `what`:
     *   { format: 'wav'|'mp3', bits, withBand: true, sr }
     */
    /* The string physics runs in slices (renderEventsAsync) so the page stays
       alive and can show progress: `what.onProgress(0..1)`. Any throw - even a
       synchronous one while the graph is built - comes back as a rejection. */
    Engine.prototype.render = function (take, what) {
        var self = this;
        try { return this._render(take, what || {}); } catch (e) { return Promise.reject(e); }
    };
    Engine.prototype._render = function (take, what) {
        var self = this;
        var C = window.EGCore;
        var core = function (list, secs, opts, from, to) {
            var prog = what.onProgress ? function (f) { what.onProgress(from + (to - from) * f); } : null;
            return C.renderEventsAsync ? C.renderEventsAsync(list, sr, secs, opts, prog) : Promise.resolve(C.renderEvents(list, sr, secs, opts));
        };
        var sr = what.sr || 48000;
        var tail = 2.5;
        var dur = Math.max(0.5, take.seconds) + tail;
        var OAC = window.OfflineAudioContext || window.webkitOfflineAudioContext;
        var oc = new OAC(2, Math.ceil(dur * sr), sr);
        var master = oc.createGain(); master.gain.value = 0.9;
        var safety = new window.EGRig.Safety(oc, -1.5);
        master.connect(safety.input); safety.output.connect(oc.destination);
        var rig = new window.EGRig.Rig(oc, { state: this.rigState || {} });
        rig.output.connect(master);

        var g = take.guitar;
        var T = window.EGCore.TUNINGS;
        if (take.di) {
            var di = new Float32Array(take.di);
            var bd = oc.createBuffer(1, di.length, take.diRate || sr);
            bd.getChannelData(0).set(di);
            var s0 = oc.createBufferSource(); s0.buffer = bd; s0.connect(rig.post); s0.start(0.0);
        }
        var drumHits = take.drums || [], bassNotes = take.bass || [];
        var band = what.withBand !== false && (drumHits.length || bassNotes.length);
        var bassEv = band ? window.EGJam.bassCoreEvents(bassNotes.filter(function (x) { return x.t >= 0 && x.t < take.seconds + 0.5; })) : [];
        var gShare = bassEv.length ? 0.8 : 1;     // progress: the guitar is most of the work
        var steps = Promise.resolve();
        if (take.events.length) {
            steps = steps.then(function () {
                return core(take.events, dur, {
                    model: g.model, sel: g.sel, tuning: T[g.tuning] || T.std, capo: g.capo, pickMm: g.pickMm,
                    intonation: g.intonation, sympathy: g.sympathy, withEnv: true, seed: 20260925
                }, 0, gShare);
            }).then(function (r) {
                var b = oc.createBuffer(1, r.di.length, sr); b.getChannelData(0).set(r.di);
                var e = oc.createBuffer(1, r.env.length, sr); e.getChannelData(0).set(r.env);
                var s1 = oc.createBufferSource(); s1.buffer = b; s1.connect(rig.input); s1.start(0);
                var s2 = oc.createBufferSource(); s2.buffer = e; s2.connect(rig.envIn); s2.start(0);
            });
        }
        if (band) {
            // the band as it was heard: its level and every hit and note it played in the take
            var jamBus = oc.createGain(); jamBus.gain.value = take.bandVol !== undefined ? take.bandVol : 0.75; jamBus.connect(master);
            var kit = this.jam && this.jam.drums;
            if (kit && kit.buffers) {
                var rr = seededRandom(20260926);
                drumHits.forEach(function (h) { if (h.t < take.seconds + 0.5) kit.hit(oc, jamBus, h.piece, h.t, h.vel, rr); });
            }
            if (bassEv.length) {
                steps = steps.then(function () {
                    return core(bassEv, dur, { model: 'bass', tuning: T.bass4, sympathy: 0, seed: 777 }, gShare, 1);
                }).then(function (rb) {
                    var bassRig = new window.EGRig.Rig(oc, { state: bassRigState() });
                    bassRig.output.connect(jamBus);
                    var bb = oc.createBuffer(1, rb.length, sr); bb.getChannelData(0).set(rb);
                    var sb = oc.createBufferSource(); sb.buffer = bb; sb.connect(bassRig.input); sb.start(0);
                });
            }
        }
        return steps.then(function () {
            // old WebKit hands the result only to oncomplete; the promise form came later
            return new Promise(function (resolve, reject) {
                oc.oncomplete = function (ev) { resolve(ev.renderedBuffer); };
                var p = oc.startRendering();
                if (p && p.then) p.then(resolve, reject);
            });
        });
    };
    // a small seeded generator: the export picks drum sample layers the same way every time
    function seededRandom(seed) {
        var s = seed >>> 0 || 1;
        return function () { s = (s * 1664525 + 1013904223) >>> 0; return s / 4294967296; };
    }
    function bassRigState() {
        return {
            guitar: { res: { f: 1900, q: 1.1 }, out: 1.6, tone: 0.9, vol: 1 },
            pedals: { comp: { on: true, sustain: 0.4, level: 0.55 }, wah: { on: false }, boost: { on: false }, drive: { on: false } },
            amp: { model: 'bass', gain: 0.35, bass: 0.62, mid: 0.5, treble: 0.45, presence: 0.35, master: 0.55, res: 0.3 },
            cab: { type: 'g12f', mic: 0.6 }, mod: { on: false }, delay: { on: false }, reverb: { on: false }, master: 0.9, trim: -3
        };
    }

    /* MIDI of a take: guitar (channel 1), bass (2), drums (10). */
    Engine.prototype.midi = function (take) {
        var W = window.TimbricaMidiWriter;
        if (!W) return null;
        var T = window.EGCore.TUNINGS, tun = T[take.guitar.tuning] || T.std;
        var cap = take.guitar.capo || 0;
        var gt = [], held = {}, vel = {};
        // by time, as the render replays them: a riff's notes are stored ahead of their time
        take.events.slice().sort(function (a, b) { return a.t - b.t; }).forEach(function (e) {
            // a tuning or capo change inside the take moves every later note with it
            if (e.type === 'tuning' && e.tuning && e.tuning.length) { tun = e.tuning; return; }
            if (e.type === 'capo') { cap = e.capo || 0; return; }
            if (e.type === 'on' || (e.type === 'slide' && held[e.s] !== undefined)) {
                /* a slide ends on another fret: in MIDI that is the next note, not
                   the first one held on (the file kept the starting pitch) */
                if (held[e.s] !== undefined) gt.push({ tMs: e.t * 1000, type: 'off', midi: held[e.s] });
                var m = tun[e.s] + cap + e.fret;
                // a natural harmonic sounds its partial, not the open string
                if (e.type === 'on' && e.o && e.o.harm > 1) m = tun[e.s] + cap + Math.round(12 * Math.log(e.o.harm) / Math.LN2);
                if (e.type === 'on') vel[e.s] = e.vel || 0.8;
                gt.push({ tMs: e.t * 1000, type: 'on', midi: m, velocity: vel[e.s] || 0.8 });
                held[e.s] = m;
            } else if (e.type === 'off' || e.type === 'muteAll') {
                var list = e.type === 'off' ? [e.s] : Object.keys(held);
                list.forEach(function (s) { if (held[s] !== undefined) { gt.push({ tMs: e.t * 1000, type: 'off', midi: held[s] }); delete held[s]; } });
            }
        });
        Object.keys(held).forEach(function (s) { gt.push({ tMs: take.seconds * 1000, type: 'off', midi: held[s] }); });
        var tracks = [{ events: gt, channel: 0, name: 'Guitar' }];
        var bpm = take.bpm || (take.jam ? take.jam.state.bpm : 120);
        if (take.bass && take.bass.length) {
            var be = [];
            take.bass.forEach(function (x) { be.push({ tMs: x.t * 1000, type: 'on', midi: x.midi, velocity: x.vel }); be.push({ tMs: (x.t + x.len) * 1000, type: 'off', midi: x.midi }); });
            tracks.push({ events: be, channel: 1, name: 'Bass' });
        }
        return W.buildMulti(tracks, bpm);
    };

    // ─────────────────────────── real guitar in ───────────────────────────
    /*
     * The same rig for a real instrument. Everything a browser does to a
     * microphone by default (echo cancellation, noise suppression, automatic
     * gain) destroys a guitar signal, so all of it is switched off; the
     * pickup filter is skipped (the real pickup already has its resonance).
     */
    /* `acquire(constraints)` opens the device - the page passes the site's
       MicManager, which names every failure (denied, no device, held by
       another app, blocked by the OS) and brings the request back when the
       person allows it later. The engine only builds the signal path. */
    Engine.prototype.openInput = function (deviceId, acquire) {
        var self = this, ctx = this.ctx;
        var c = { echoCancellation: false, noiseSuppression: false, autoGainControl: false, channelCount: 1, latency: 0 };
        if (deviceId) c.deviceId = { exact: deviceId };
        if (typeof acquire !== 'function') return Promise.reject(new Error('no microphone manager'));
        return acquire({ audio: c }).then(function (stream) {
            self.closeInput();
            var src = ctx.createMediaStreamSource(stream);
            var trim = ctx.createGain(); trim.gain.value = 1;
            var hp = ctx.createBiquadFilter(); hp.type = 'highpass'; hp.frequency.value = 28; hp.Q.value = 0.6; // below a bass's low E (41 Hz)
            var gate = null, rec = null;
            try {
                gate = new AudioWorkletNode(ctx, 'eg-gate', { numberOfInputs: 1, numberOfOutputs: 1, outputChannelCount: [1] });
            } catch (e) { gate = ctx.createGain(); }
            try {
                rec = new AudioWorkletNode(ctx, 'eg-rec', { numberOfInputs: 1, numberOfOutputs: 1, outputChannelCount: [1] });
            } catch (e) { rec = null; }
            // the tuner's frame: 4096 for a low B (2048 where the browser will not resize it)
            var an = window.EGRig.analyser(ctx, 4096);
            src.connect(trim); trim.connect(hp); hp.connect(gate); if (an) hp.connect(an);
            gate.connect(self.rig.post);
            var mute = null;
            if (rec) { hp.connect(rec); mute = ctx.createGain(); mute.gain.value = 0; rec.connect(mute); mute.connect(ctx.destination); }
            self.input = { stream: stream, src: src, trim: trim, gate: gate, analyser: an, recorder: rec, recMute: mute, hp: hp };
            var tr = stream.getAudioTracks()[0];
            var st = tr && tr.getSettings ? tr.getSettings() : {};
            self.input.latency = Math.round(((st.latency || 0) + (ctx.baseLatency || 0) + (ctx.outputLatency || 0)) * 1000);
            self.input.label = tr ? tr.label : '';
            return self.input;
        });
    };
    Engine.prototype.closeInput = function () {
        var i = this.input;
        if (!i) return;
        try { i.stream.getTracks().forEach(function (t) { t.stop(); }); } catch (e) {}
        try { i.src.disconnect(); i.gate.disconnect(); i.hp.disconnect(); i.trim.disconnect(); } catch (e) {}
        /* the recorder worklet and its silent route to the destination too: they
           used to stay connected with process() returning true - one more running
           worklet for every input opened or device switched */
        try { if (i.recorder) { i.recorder.port.postMessage({ type: 'close' }); i.recorder.disconnect(); } if (i.recMute) i.recMute.disconnect(); } catch (e) {}
        this.input = null;
    };
    Engine.prototype.inputLevel = function () {
        var i = this.input;
        if (!i) return null;
        var r = window.EGRig.readLevel(i.analyser, this._il);
        if (!r) return null;
        this._il = r.buf;
        return { peak: r.peak, buf: r.buf, sr: this.ctx.sampleRate };
    };

    return {
        VERSION: 1,
        Engine: Engine,
        expandRiff: expandRiff,
        wavBlob: wavBlob,
        midiToHz: midiToHz
    };
}));
